import { useCallback } from 'react';

import { useTrackSwitchedVersion } from '@air/analytics';

import { type GoToPublicAssetPageProps, useGoToPublicAssetPage } from '~/hooks/useGoToPublicAssetPage';

/**
 * Public-area owner of version switching. Deliberately duplicated from the private
 * twin (`useSwitchPrivateVersion`) per rules/area-isolation.md — each calls its own
 * area's goToAssetPage; `trackSwitchedVersion` is an external contract shared by both.
 */
export type SwitchPublicVersionProps = {
  version: GoToPublicAssetPageProps['asset'];
  location: 'tab' | 'dropdown' | 'mobile' | 'discussion' | 'header';
  query?: GoToPublicAssetPageProps['query'];
};

export const useSwitchPublicVersion = () => {
  const { trackSwitchedVersion } = useTrackSwitchedVersion();
  const { goToAssetPage } = useGoToPublicAssetPage();

  const switchVersion = useCallback(
    ({ version, location, query }: SwitchPublicVersionProps) => {
      goToAssetPage({
        asset: version,
        query,
      });
      trackSwitchedVersion({ location });
    },
    [goToAssetPage, trackSwitchedVersion],
  );

  return {
    switchVersion,
  };
};
